/**
 * Startup performance timeline — logs duration, category, and slow thresholds.
 * Open DevTools → Console; filter "[startup]" after load.
 */

const THRESHOLD_WARN_MS = 250;
const THRESHOLD_SLOW_MS = 1000;
const THRESHOLD_CRITICAL_MS = 3000;

export const APP_START_MS =
  typeof performance !== 'undefined' && performance.timeOrigin
    ? performance.timeOrigin
    : Date.now();

/** Pre-fix estimates from static audit (Jun 2026). */
export const STARTUP_PERF_BASELINE_MS = {
  'js.bundle': 1500,
  'app.firstRender': 2200,
  'auth.onAuthStateChanged': 500,
  'firestore.subscribeUserProfile': 400,
  'firestore.userRealtime': 400,
  'firestore.socialListeners': 1200,
  'lockbox.chunkLoad': 800,
  'api.getHomepageData': 350,
  'firestore.games': 400,
  'firestore.subscribeArenaChat': 800,
  'timeToVisual': 300,
  'cachedRender': 5,
  'hydration.priority': 16,
  'hydration.primary': 16,
  'apiFetch': 350,
  'pwa.splashVisible': 1200,
  'pwa.firstMeaningfulPaint': 300,
  'pwa.timeToInteractive': 800,
  'route.change': 200,
};

const pending = new Map();
const completed = [];
const timeline = [];

function now() {
  return typeof performance !== 'undefined' ? performance.now() : 0;
}

function sinceAppStartMs() {
  return Math.round(now());
}

function formatMs(ms) {
  return `${Math.round(ms)}ms`;
}

function levelFor(ms) {
  if (ms >= THRESHOLD_CRITICAL_MS) return 'CRITICAL';
  if (ms >= THRESHOLD_SLOW_MS) return 'SLOW';
  if (ms >= THRESHOLD_WARN_MS) return 'WARN';
  return 'ok';
}

/** Absolute timeline event (ms since navigation start). */
export function perfTimeline(label, category = 'render', meta = {}) {
  const at = sinceAppStartMs();
  const entry = { label, category, atMs: at, meta };
  timeline.push(entry);
  console.info(`[startup] timeline +${formatMs(at)} ${label} [${category}]`, meta);
  return entry;
}

export function perfMarkStart(name, meta = {}) {
  pending.set(name, { start: now(), meta });
  if (typeof performance !== 'undefined' && performance.mark) {
    performance.mark(`${name}:start`);
  }
  if (typeof console !== 'undefined' && console.time) {
    try {
      console.time(`[startup] ${name}`);
    } catch (_) {}
  }
}

export function perfMarkEnd(name, category = 'render', meta = {}) {
  const entry = pending.get(name);
  if (!entry) return null;
  pending.delete(name);
  const durationMs = now() - entry.start;
  if (typeof console !== 'undefined' && console.timeEnd) {
    try {
      console.timeEnd(`[startup] ${name}`);
    } catch (_) {}
  }
  const record = {
    name,
    category,
    durationMs,
    atMs: sinceAppStartMs(),
    level: levelFor(durationMs),
    meta: { ...entry.meta, ...meta },
    baselineMs: STARTUP_PERF_BASELINE_MS[name] ?? null,
  };
  completed.push(record);
  timeline.push({
    label: `${name} (${formatMs(durationMs)})`,
    category,
    atMs: record.atMs,
    meta: record.meta,
  });
  if (typeof performance !== 'undefined' && performance.mark && performance.measure) {
    performance.mark(`${name}:end`);
    try {
      performance.measure(name, `${name}:start`, `${name}:end`);
    } catch (_) {}
  }
  const baseline = record.baselineMs;
  const delta =
    baseline != null
      ? ` (baseline ${formatMs(baseline)}, ${durationMs - baseline >= 0 ? '+' : ''}${Math.round(durationMs - baseline)}ms)`
      : '';
  const tag = record.level === 'ok' ? 'info' : 'warn';
  console[tag](
    `[startup] ${name} ${formatMs(durationMs)} [${category}] ${record.level}${delta}`,
    record.meta
  );
  return record;
}

export async function perfAsync(name, category, fn, meta = {}) {
  perfMarkStart(name, meta);
  try {
    const result = await fn();
    perfMarkEnd(name, category, { ...meta, ok: true });
    return result;
  } catch (err) {
    perfMarkEnd(name, category, { ...meta, ok: false, error: err?.message || String(err) });
    throw err;
  }
}

export function perfAttachWebVitals(metric) {
  const record = {
    name: `web-vital.${metric.name}`,
    category: 'render',
    durationMs: metric.value,
    level: levelFor(metric.value),
    meta: { id: metric.id, rating: metric.rating },
    baselineMs: null,
  };
  completed.push(record);
  perfTimeline(`web-vital.${metric.name}=${formatMs(metric.value)}`, 'render', record.meta);
}

export function perfLogSummary() {
  const sorted = [...completed].sort((a, b) => b.durationMs - a.durationMs);
  const firstRender = completed.find((r) => r.name === 'app.firstRender');
  const timeToVisual = firstRender?.durationMs ?? null;

  console.group('[startup] Performance summary');
  console.info('[startup] App start → summary:', formatMs(sinceAppStartMs()));

  console.group('[startup] Timeline (chronological)');
  console.table(
    [...timeline].map((t) => ({
      at: `+${formatMs(t.atMs)}`,
      event: t.label,
      category: t.category,
    }))
  );
  console.groupEnd();

  console.table(
    sorted.map((r) => ({
      step: r.name,
      category: r.category,
      duration: formatMs(r.durationMs),
      level: r.level,
      baseline: r.baselineMs != null ? formatMs(r.baselineMs) : '—',
      delta:
        r.baselineMs != null
          ? `${r.durationMs - r.baselineMs >= 0 ? '+' : ''}${Math.round(r.durationMs - r.baselineMs)}ms`
          : '—',
    }))
  );

  if (timeToVisual != null) {
    const target = STARTUP_PERF_BASELINE_MS.timeToVisual;
    const ok = timeToVisual <= target;
    console[ok ? 'info' : 'warn'](
      `[startup] Time to visual (firstRender): ${formatMs(timeToVisual)} (goal <${target}ms)`
    );
  }

  const fmp = completed.find((r) => r.name === 'pwa.firstMeaningfulPaint');
  const tti = completed.find((r) => r.name === 'pwa.timeToInteractive');
  const installEvt = timeline.find((t) => t.label === 'pwa.installPromptAvailable');
  if (fmp || tti || installEvt) {
    console.group('[startup] PWA UX metrics');
    if (fmp) {
      console.info(`[startup] First meaningful paint: ${formatMs(fmp.durationMs)}`);
    }
    if (tti) {
      console.info(`[startup] Time to interactive: ${formatMs(tti.durationMs)}`);
    }
    console.info(
      `[startup] Install prompt available: ${installEvt ? 'yes' : 'no (criteria not met or already installed)'}`
    );
    console.groupEnd();
  }

  const improved = sorted.filter(
    (r) => r.baselineMs != null && r.durationMs < r.baselineMs * 0.85
  );
  if (improved.length) {
    console.info(
      '[startup] Improved vs baseline:',
      improved.map((r) => `${r.name} ${formatMs(r.durationMs)}`).join(', ')
    );
  }
  console.groupEnd();
}
